import { type DungeonEvent, type DungeonView, idx, MAP_H, MAP_W, type Pos } from '@xox/shared'
import {
  type HTMLAttributes,
  memo,
  type ReactElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { DungeonDefs, ITEM_TINT, type Sprite } from './art/Sprites'
import { cameraFor, VIEW_H, VIEW_W } from './camera'

/** How long a damage number floats and a hit shakes. */
const FLOAT_MS = 700
const CAMERA_MS = 140
const STEP_MS = 120

interface Float {
  key: number
  pos: Pos
  text: string
  tone: 'hero' | 'monster' | 'heal' | 'gold'
}

type Props = HTMLAttributes<HTMLDivElement> & {
  view: DungeonView
  /** Events since the last render; the board turns them into numbers and shakes. */
  fx: readonly DungeonEvent[]
}

/**
 * The dungeon as one SVG: a camera window that follows the hero over the seen part of the map.
 * Tiles are `<use>` references to shared symbols; remembered-but-unseen cells get fog.
 */
export const DungeonBoard = memo(function DungeonBoard({
  view,
  fx,
  className = '',
  ...rest
}: Props) {
  const prefix = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const cam = cameraFor(view.hero.pos)
  const [floats, setFloats] = useState<Float[]>([])
  const [shaking, setShaking] = useState<Set<number>>(new Set())
  const nextKey = useRef(0)

  useEffect(() => {
    if (fx.length === 0) return
    const added: Float[] = []
    const shook = new Set<number>()
    const monsterPos = (id: number) => view.monsters.find((m) => m.id === id)?.pos
    for (const e of fx) {
      switch (e.type) {
        case 'heroAttacked':
        case 'monsterHurt': {
          const pos = monsterPos(e.id) ?? view.hero.pos
          added.push({
            key: nextKey.current++,
            pos,
            text: `-${e.dmg}${'crit' in e && e.crit ? '!' : ''}`,
            tone: 'monster',
          })
          shook.add(e.id)
          break
        }
        case 'heroHurt':
          added.push({
            key: nextKey.current++,
            pos: view.hero.pos,
            text: `-${e.dmg}`,
            tone: 'hero',
          })
          break
        case 'healed':
          added.push({
            key: nextKey.current++,
            pos: view.hero.pos,
            text: `+${e.amount}`,
            tone: 'heal',
          })
          break
        case 'gold':
          added.push({ key: nextKey.current++, pos: e.pos, text: `+${e.amount}`, tone: 'gold' })
          break
        default:
          break
      }
    }
    if (added.length > 0) setFloats((f) => [...f, ...added])
    if (shook.size > 0) setShaking(shook)
    const t = setTimeout(() => {
      setFloats((f) => f.filter((x) => !added.includes(x)))
      setShaking(new Set())
    }, FLOAT_MS)
    return () => clearTimeout(t)
  }, [fx, view.monsters, view.hero.pos])

  const use = (sym: Sprite, p: Pos, extra: Record<string, unknown> = {}) => (
    <use href={`#${prefix}-${sym}`} x={p.c} y={p.r} width={1} height={1} {...extra} />
  )

  const tiles: ReactElement[] = []
  const fog: ReactElement[] = []
  for (let r = Math.max(0, cam.r - 1); r < Math.min(MAP_H, cam.r + VIEW_H + 1); r++) {
    for (let c = Math.max(0, cam.c - 1); c < Math.min(MAP_W, cam.c + VIEW_W + 1); c++) {
      const i = idx({ r, c })
      const t = view.tiles[i]
      if (t === ' ' || t === undefined) continue
      const sym: Sprite =
        t === '#'
          ? 'wall'
          : t === '>'
            ? 'stairs'
            : t === 'X'
              ? 'sealed'
              : t === 'C'
                ? 'chest'
                : (r + c) % 2
                  ? 'floorAlt'
                  : 'floor'
      tiles.push(<g key={i}>{use(sym, { r, c })}</g>)
      if (view.visible[i] !== '1') {
        fog.push(<rect key={`f${i}`} x={c} y={r} width={1} height={1} fill="var(--dg-fog)" />)
      }
    }
  }

  const alive = view.monsters.length
  return (
    <div
      role="img"
      aria-label={`Floor ${view.floor}, ${view.hero.hp} of ${view.hero.maxHp} HP, ${alive} ${alive === 1 ? 'monster' : 'monsters'} in view`}
      className={`relative select-none overflow-hidden rounded-3xl bg-[var(--dg-dark)] shadow-inner ring-1 ring-black/20 ${className}`}
      style={{
        width: 'min(92vw, 400px)',
        aspectRatio: `${VIEW_W} / ${VIEW_H}`,
        touchAction: 'none',
      }}
      {...rest}
    >
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="absolute inset-0 size-full" aria-hidden>
        <DungeonDefs prefix={prefix} />
        <g
          style={{
            transform: `translate(${-cam.c}px, ${-cam.r}px)`,
            transition: `transform ${CAMERA_MS}ms ease-out`,
          }}
        >
          {tiles}
          {view.items.map((it) =>
            it.item === 'gold' ? (
              <g key={`${it.pos.r}-${it.pos.c}-gold`}>{use('gold', it.pos)}</g>
            ) : (
              <g key={`${it.pos.r}-${it.pos.c}-${it.item}`} style={{ color: ITEM_TINT[it.item] }}>
                {use('bag', it.pos)}
              </g>
            ),
          )}
          {fog}
          {view.monsters.map((m) => (
            <g
              key={m.id}
              className={shaking.has(m.id) ? 'animate-shake' : ''}
              style={{
                transform: `translate(${m.pos.c}px, ${m.pos.r}px)`,
                transition: `transform ${STEP_MS}ms ease-out`,
                opacity: m.frozen > 0 ? 0.75 : 1,
              }}
            >
              {use(m.kind, { r: 0, c: 0 })}
              {m.frozen > 0 && <rect width={1} height={1} fill="var(--dg-frost)" opacity=".35" />}
              <rect x={0.1} y={0.02} width={0.8} height={0.08} fill="var(--dg-dark)" />
              <rect
                x={0.1}
                y={0.02}
                width={0.8 * Math.max(0, m.hp / m.maxHp)}
                height={0.08}
                fill={m.boss ? 'var(--dg-gold)' : 'var(--dg-blood)'}
              />
            </g>
          ))}
          <g
            style={{
              transform: `translate(${view.hero.pos.c}px, ${view.hero.pos.r}px)`,
              transition: `transform ${STEP_MS}ms ease-out`,
            }}
          >
            {use('hero', { r: 0, c: 0 })}
          </g>
          {floats.map((f) => (
            <text
              key={f.key}
              x={f.pos.c + 0.5}
              y={f.pos.r + 0.35}
              textAnchor="middle"
              fontSize="0.55"
              fontWeight="900"
              className="animate-float-up"
              fill={
                f.tone === 'hero'
                  ? 'var(--dg-blood)'
                  : f.tone === 'heal'
                    ? 'var(--dg-slime)'
                    : f.tone === 'gold'
                      ? 'var(--dg-gold)'
                      : 'white'
              }
              stroke="var(--dg-dark)"
              strokeWidth="0.06"
              paintOrder="stroke"
            >
              {f.text}
            </text>
          ))}
        </g>
      </svg>
    </div>
  )
})
